import { useCallback, useEffect, useRef, useState } from 'react'
import { Menu, PanelRight, ArrowUp, Plane, FlaskConical, HeartPulse, MapPin, Mic, Phone, Video, type LucideIcon } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { AIAvatar } from '@/components/ui/AIAvatar'
import { CompanionAvatar } from '@/components/CompanionAvatar'
import { CoachOrb } from '@/components/CoachOrb'
import { AvatarCallScreen, type CallPhase } from '@/components/AvatarCallScreen'
import { VoiceCallScreen } from '@/components/VoiceCallScreen'
import type { TalkingAvatarHandle, AvatarStatus } from '@/components/TalkingAvatar'
import { cn } from '@/lib/utils'
import { streamChat, type ChatMsg } from '@/api/chat'
import { speak, stopSpeaking } from '@/api/voice'
import { avatarAvailable } from '@/api/avatar'
import { useSpeechInput } from '@/lib/useSpeechInput'
import { useConversationLoop } from '@/lib/useConversationLoop'
import { classifyMood, type Mood } from '@/lib/mood'
import { companionById, type Companion } from '../data/companions'

type CallMode = 'voice' | 'video' | null

const USER_ID = 'demo-user'

const SUGGESTIONS: { icon: LucideIcon; text: string }[] = [
  { icon: Plane, text: 'I have a 16-hour flight to Tokyo next week' },
  { icon: FlaskConical, text: 'What does an A1C of 6.1 actually mean?' },
  { icon: HeartPulse, text: "I've had chest pain since this morning" },
  { icon: MapPin, text: 'Help me find a cardiologist near me' },
]

type Turn = {
  role: 'user' | 'assistant'
  content: string
  persona?: string
  streaming?: boolean
  at?: number
}

const fmtTime = (at?: number) =>
  at ? new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''

const GREETING: Turn = {
  role: 'assistant',
  persona: 'coach',
  content:
    "Morning, Alex. I noticed your sleep dipped under 6 hours and your BP ran a little high yesterday. Want to talk through it — or is something else on your mind today?",
}

export function ChatPanel({
  coach,
  userName,
  onMenu,
  showMenu,
  onToggleCanvas,
  canvasOpen,
  initialPrompt,
}: {
  coach: string
  userName?: string
  onMenu: () => void
  showMenu: boolean
  onToggleCanvas: () => void
  canvasOpen: boolean
  initialPrompt?: string
}) {
  const coachC = companionById(coach)
  const firstName = (userName || '').trim().split(' ')[0]
  const [turns, setTurns] = useState<Turn[]>(() => {
    let greet = coachC?.greeting ?? GREETING.content
    if (firstName) greet = greet.replace(/\bAlex\b/g, firstName)
    return [{ role: 'assistant', persona: 'coach', content: greet, at: Date.now() }]
  })
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [mood, setMood] = useState<Mood>('neutral') // how the coach visibly reacts to the latest reply
  const [videoAvailable, setVideoAvailable] = useState(false)
  const [activeCall, setActiveCall] = useState<CallMode>(null) // 'voice' | 'video' | null
  const [callPhase, setCallPhase] = useState<CallPhase>('connecting')
  const [callMuted, setCallMuted] = useState(false)
  const activeCallRef = useRef(activeCall)
  activeCallRef.current = activeCall
  const avatarRef = useRef<TalkingAvatarHandle>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => { avatarAvailable().then(setVideoAvailable) }, [])

  const scrollToEnd = () => {
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }))
  }

  // NB: the effect callback must NOT return the rAF id (a number) — React would treat it as a
  // cleanup function and crash ("destroy is not a function") on the next render.
  useEffect(() => {
    scrollToEnd()
  }, [turns])

  // Smooth typewriter: network chunks land in a buffer; a rAF loop reveals characters at a
  // steady cadence so text flows smoothly regardless of how chunky the stream arrives.
  const bufRef = useRef({ target: '', shown: 0, done: true })
  const rafRef = useRef<number | null>(null)

  const pump = useCallback(() => {
    const b = bufRef.current
    if (b.shown < b.target.length) {
      const step = Math.max(2, Math.ceil((b.target.length - b.shown) / 9)) // catch-up, eases out
      b.shown = Math.min(b.target.length, b.shown + step)
      const text = b.target.slice(0, b.shown)
      setTurns((prev) => {
        const n = [...prev]; const i = n.length - 1
        n[i] = { ...n[i], content: text }
        return n
      })
    }
    if (b.shown < b.target.length || !b.done) {
      rafRef.current = requestAnimationFrame(pump)
    } else {
      rafRef.current = null
      setTurns((prev) => { const n = [...prev]; const i = n.length - 1; n[i] = { ...n[i], streaming: false }; return n })
      setBusy(false)
      // Call speech is dispatched incrementally as sentences complete (see enqueueSpeech below) —
      // by the time the typewriter catches up, speaking has usually already started.
    }
  }, [])

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }, [])

  // ── Call speech pipeline: speak each sentence as soon as it's complete, instead of waiting for
  // the whole reply — cuts the "thinking" gap from the full reply+full TTS down to ~one sentence.
  const speechBufRef = useRef('')       // text received but not yet split into a queued sentence
  const speechQueueRef = useRef<string[]>([])
  const speakingNowRef = useRef(false)
  const streamDoneRef = useRef(true)

  const drainSpeechQueue = useCallback(() => {
    if (speakingNowRef.current) return
    if (!activeCallRef.current) { speechQueueRef.current = []; return } // call ended — stop talking
    const next = speechQueueRef.current.shift()
    if (next === undefined) {
      if (streamDoneRef.current) setCallPhase('listening') // nothing left to say — resume listening
      return
    }
    speakingNowRef.current = true
    setCallPhase('speaking')
    const mode = activeCallRef.current
    const spoken = mode === 'video' ? avatarRef.current?.speak(next) : speak(next, coach, {})
    Promise.resolve(spoken).finally(() => {
      speakingNowRef.current = false
      drainSpeechQueue()
    })
  }, [coach])

  const enqueueSpeech = useCallback((text: string) => {
    if (!activeCallRef.current || !text.trim()) return
    speechQueueRef.current.push(text.trim())
    drainSpeechQueue()
  }, [drainSpeechQueue])

  // Split on sentence-ending punctuation; the trailing fragment (no terminal punctuation yet)
  // is held back until more text arrives or the stream ends.
  const splitSentences = (buf: string): { sentences: string[]; rest: string } => {
    const parts = buf.split(/(?<=[.!?])\s+/)
    if (parts.length <= 1) return { sentences: [], rest: buf }
    const rest = parts.pop() || ''
    return { sentences: parts, rest }
  }

  const send = useCallback(
    (text: string) => {
      if (!text.trim() || busy) return
      stopSpeaking() // barge-in: cut off any current speech
      if (activeCallRef.current) setCallPhase('thinking')
      setMood('neutral')
      setInput('')
      setBusy(true)
      setTurns((prev) => [
        ...prev,
        { role: 'user', content: text, at: Date.now() },
        { role: 'assistant', content: '', streaming: true, at: Date.now() },
      ])
      bufRef.current = { target: '', shown: 0, done: false }
      speechBufRef.current = ''
      speechQueueRef.current = []
      streamDoneRef.current = false

      streamChat(USER_ID, [{ role: 'user', content: text }], {
        onMeta: (m) =>
          setTurns((prev) => {
            const next = [...prev]
            next[next.length - 1] = { ...next[next.length - 1], persona: m.persona }
            return next
          }),
        onDelta: (chunk) => {
          bufRef.current.target += chunk
          if (rafRef.current == null) rafRef.current = requestAnimationFrame(pump)
          if (activeCallRef.current) {
            speechBufRef.current += chunk
            const { sentences, rest } = splitSentences(speechBufRef.current)
            if (sentences.length) {
              speechBufRef.current = rest
              sentences.forEach(enqueueSpeech)
            }
          }
        },
        onDone: () => {
          bufRef.current.done = true
          if (rafRef.current == null) rafRef.current = requestAnimationFrame(pump)
          setMood(classifyMood(bufRef.current.target))
          if (activeCallRef.current) {
            streamDoneRef.current = true
            const leftover = speechBufRef.current
            speechBufRef.current = ''
            if (leftover.trim()) enqueueSpeech(leftover)
            else drainSpeechQueue() // queue may already be empty — flip back to listening
          }
        },
        onError: (err) => {
          bufRef.current.done = true
          setTurns((prev) => {
            const next = [...prev]
            next[next.length - 1] = { role: 'assistant', content: `⚠️ ${err}`, streaming: false }
            return next
          })
          setBusy(false)
          if (activeCallRef.current) {
            streamDoneRef.current = true
            speechBufRef.current = ''
            enqueueSpeech(err) // say the error aloud too — a call has no visible red text to read
          }
        },
      }, { companion: coach, inCall: !!activeCallRef.current })
    },
    [busy, pump, coach, enqueueSpeech, drainSpeechQueue],
  )

  // Speech-to-text: mic → transcript → send. Hands the final utterance straight to send().
  const { supported: micSupported, listening, transcript, toggle: toggleMic } = useSpeechInput((text) => send(text))

  // Hands-free conversation loop for voice/video calls: only listens while callPhase is
  // 'listening' and the mic isn't muted, so it never captures the coach's own voice while it's
  // speaking. Works identically for either call mode — only the speak-output path differs.
  const { transcript: callTranscript } = useConversationLoop(
    activeCall !== null && callPhase === 'listening' && !callMuted,
    (text) => send(text),
  )

  // Stop any audio when leaving the chat.
  useEffect(() => () => { stopSpeaking() }, [])

  const toggleCall = (mode: 'voice' | 'video') => {
    if (activeCall === mode) {
      setActiveCall(null) // unmounts the call screen, which disconnects/stops itself on cleanup
    } else {
      stopSpeaking()
      setCallMuted(false)
      // Voice calls have nothing to connect to — go straight to listening. Video calls wait for
      // the LiveKit session to come up (handleAvatarStatus flips this to 'listening' once live).
      setCallPhase(mode === 'voice' ? 'listening' : 'connecting')
      setActiveCall(mode)
    }
  }

  const handleAvatarStatus = useCallback((status: AvatarStatus) => {
    if (status === 'live') setCallPhase('listening')
    else if (status === 'error') setCallPhase('error')
    else setCallPhase('connecting')
  }, [])

  // Connect only after <TalkingAvatar> has actually mounted and the ref is populated — calling
  // connect() synchronously right after setActiveCall('video') would race React's commit and hit
  // a stale/null ref.
  useEffect(() => {
    if (activeCall === 'video') avatarRef.current?.connect()
  }, [activeCall])

  // Auto-ask when arriving from "Ask SARA about this" (parent remounts with a fresh prompt).
  useEffect(() => {
    if (initialPrompt) send(initialPrompt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Full-screen call — takes over the whole panel (its own header/controls), like a real 1:1
  // call rather than a chat with a video thumbnail bolted on. Voice calls skip the LiveKit video
  // session entirely and just speak through the existing TTS path.
  if (activeCall === 'voice' && coachC) {
    return (
      <div className="flex h-full flex-col bg-black">
        <VoiceCallScreen
          coach={coachC}
          phase={callPhase}
          mood={mood}
          transcript={callTranscript}
          muted={callMuted}
          onToggleMute={() => setCallMuted((m) => !m)}
          onEndCall={() => toggleCall('voice')}
        />
      </div>
    )
  }
  if (activeCall === 'video' && coachC) {
    return (
      <div className="flex h-full flex-col bg-black">
        <AvatarCallScreen
          ref={avatarRef}
          coach={coachC}
          phase={callPhase}
          mood={mood}
          transcript={callTranscript}
          muted={callMuted}
          onToggleMute={() => setCallMuted((m) => !m)}
          onEndCall={() => toggleCall('video')}
          onStatusChange={handleAvatarStatus}
        />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-surface-chat">
      {/* Header */}
      <header className="flex items-center gap-2 h-16 px-4 border-b border-border shrink-0">
        {showMenu && (
          <button onClick={onMenu} className="p-2 -ml-2 rounded-md hover:bg-muted" aria-label="Menu">
            <Menu className="size-5 text-foreground" />
          </button>
        )}
        {coachC ? (
          <CoachOrb active={busy} color={coachC.accent}><CompanionAvatar c={coachC} size={32} mood={mood} /></CoachOrb>
        ) : (
          <span className={cn('sara-star', busy && 'sara-star-active')}><AIAvatar size="sm" /></span>
        )}
        <div className="flex-1 leading-tight">
          <div className="text-sm font-semibold text-foreground">{coachC?.name ?? 'SARA'}</div>
          <div className="text-[11px] text-muted-foreground">
            {listening ? 'Listening…' : (coachC?.tagline ?? 'Your personal health coach')}
          </div>
        </div>
        <button
          onClick={() => toggleCall('voice')}
          className="p-2 rounded-md hover:bg-muted"
          aria-label="Start voice call"
          title={`Talk out loud with ${coachC?.name ?? 'SARA'}`}
        >
          <Phone className="size-5 text-muted-foreground" />
        </button>
        {videoAvailable && coachC && (
          <button
            onClick={() => toggleCall('video')}
            className="p-2 rounded-md hover:bg-muted"
            aria-label="Start video call"
            title={`Talk face-to-face with ${coachC.name}`}
          >
            <Video className="size-5 text-muted-foreground" />
          </button>
        )}
        <button
          onClick={onToggleCanvas}
          className={cn('p-2 rounded-md hover:bg-muted', canvasOpen && 'bg-accent')}
          aria-label="Toggle health canvas"
        >
          <PanelRight className="size-5 text-foreground" />
        </button>
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-6 space-y-5">
          {turns.map((t, i) => (
            <MessageBubble key={i} turn={t} coach={coachC} mood={i === turns.length - 1 ? mood : 'neutral'} />
          ))}

          {turns.length <= 1 && (
            <div className="grid grid-cols-1 gap-2.5 pt-2 animate-fade-up">
              {SUGGESTIONS.map(({ icon: Icon, text }) => (
                <button
                  key={text}
                  onClick={() => send(text)}
                  className="lift flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent">
                    <Icon className="size-4 text-accent-foreground" />
                  </span>
                  <span className="text-sm text-foreground">{text}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-border bg-card px-4 py-3">
        <div className="mx-auto max-w-3xl flex items-end gap-2">
          {micSupported ? (
            <button
              onClick={toggleMic}
              disabled={busy}
              className={cn(
                'flex size-11 shrink-0 items-center justify-center rounded-full border transition-all active:scale-95 disabled:opacity-40',
                listening ? 'border-transparent bg-alert text-white mic-pulse' : 'border-border bg-card text-foreground hover:bg-muted',
              )}
              aria-label={listening ? 'Stop listening' : 'Speak to SARA'}
              title={listening ? 'Listening… tap to stop' : 'Speak'}
            >
              <Mic className="size-5" />
            </button>
          ) : null}
          <textarea
            value={listening ? transcript : input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                send(input)
              }
            }}
            readOnly={listening}
            rows={1}
            placeholder={listening ? 'Listening…' : `Tell ${coachC?.name ?? 'SARA'} how you're feeling…`}
            className="flex-1 resize-none max-h-40 rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
          />
          <button
            onClick={() => send(input)}
            disabled={busy || !input.trim()}
            className="flex size-11 shrink-0 items-center justify-center rounded-full sara-gradient text-white shadow-sm transition-all hover:brightness-105 active:scale-95 disabled:opacity-40 disabled:saturate-50"
            aria-label="Send"
          >
            <ArrowUp className="size-5" />
          </button>
        </div>
        <p className="mx-auto max-w-3xl pt-2 text-[11px] text-muted-foreground text-center">
          SARA offers coaching and navigation, not diagnosis. For medical decisions, consult a clinician.
        </p>
      </div>
    </div>
  )
}

function MessageBubble({ turn, coach, mood = 'neutral' }: { turn: Turn; coach?: Companion; mood?: Mood }) {
  const isUser = turn.role === 'user'

  if (isUser) {
    return (
      <div className="flex flex-col items-end animate-fade-up">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-sm text-primary-foreground shadow-sm">
          {turn.content}
        </div>
        {turn.at && <span className="mt-1 mr-1 text-[10px] text-muted-foreground">{fmtTime(turn.at)}</span>}
      </div>
    )
  }

  const name = coach?.name ?? 'SARA'
  return (
    <div className="flex gap-3 animate-fade-up">
      <span className="mt-1">
        {coach && turn.streaming
          ? <CoachOrb active color={coach.accent}><CompanionAvatar c={coach} size={28} mood={mood} /></CoachOrb>
          : coach ? <CompanionAvatar c={coach} size={28} mood={mood} /> : <AIAvatar size="sm" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-xs font-semibold" style={{ color: coach?.accent ?? 'var(--foreground)' }}>
            {name}
          </span>
          {turn.at && <span className="text-[10px] text-muted-foreground">{fmtTime(turn.at)}</span>}
        </div>
        <div className="prose-sara text-sm leading-relaxed text-foreground">
          {turn.content ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.content}</ReactMarkdown>
          ) : (
            <Working persona={name} />
          )}
        </div>
      </div>
    </div>
  )
}

// Persona-aware "SARA is working" status — rotates through real-feeling steps while Opus thinks,
// so the wait never feels stuck. Replaced by streamed text the moment the first token arrives.
function Working({ persona }: { persona: string }) {
  const steps = [
    'Reading your health record',
    'Reviewing your 7-day trends',
    'Connecting the dots across your data',
    `${persona} is composing your answer`,
  ]
  const [i, setI] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1 < steps.length ? v + 1 : v)), 1400)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div className="flex items-center gap-2 py-0.5">
      <span className="flex gap-1">
        <span className="sara-dot size-1.5 rounded-full bg-ornament-plum" style={{ animationDelay: '0ms' }} />
        <span className="sara-dot size-1.5 rounded-full bg-ornament-plum" style={{ animationDelay: '160ms' }} />
        <span className="sara-dot size-1.5 rounded-full bg-ornament-plum" style={{ animationDelay: '320ms' }} />
      </span>
      <span className="sara-shimmer text-[13px] font-medium">{steps[i]}…</span>
    </div>
  )
}
